import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Reorder, useDragControls } from "framer-motion";
import { Pencil, Trash2, Clock, MapPin, Monitor, AlertTriangle, User, Users, ChevronUp, ChevronDown, ChevronsUpDown, Columns3, GripVertical, Eye, EyeOff, RotateCcw } from "lucide-react";
import { fmtDate, fmtDateOnly } from "@/lib/time";
import api, { apiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { TICKET_STATUS, statusMeta, categoryMeta, prioMeta, isOverdue, assigneeLabel, slaState } from "@/lib/tickets";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const dash = <span className="text-gray-600">—</span>;
const PRIO_RANK = { high: 0, normal: 1, low: 2 };
const STATUS_RANK = { open: 0, in_progress: 1, waiting: 2, closed: 3 };

function slaCell(dueIso, breached, closed, de) {
  if (!dueIso) return <span className="text-gray-600">—</span>;
  const st = slaState(dueIso, breached, closed);
  const cls = st?.level === "breached" ? "border-red-500/40 bg-red-500/15 text-red-700"
    : st?.level === "soon" ? "border-amber-500/40 bg-amber-500/15 text-amber-700"
    : "border-gray-200 bg-gray-50 text-gray-700";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border font-mono whitespace-nowrap ${cls}`}>
      {st?.level === "breached" && <AlertTriangle className="w-3 h-3" />}
      <Clock className="w-3 h-3" /> {fmtDate(dueIso)}
    </span>
  );
}

// Reihenfolge = Wichtigkeit (wichtigste zuerst). Standardmaessig ausgeblendet = weniger wichtig.
const ALL_KEYS = ["status", "title", "location", "priority", "due_date", "sla_customer", "sla_internal", "category", "device", "assignee", "created_at"];
const DEFAULT_HIDDEN = ["sla_customer", "sla_internal", "category", "device", "assignee", "created_at"];
const ORDER_KEY = "ticket_cols_order_v1";
const HIDDEN_KEY = "ticket_cols_hidden_v1";

function cmpVals(a, b) {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function loadOrder() {
  try {
    const saved = JSON.parse(localStorage.getItem(ORDER_KEY));
    if (Array.isArray(saved)) {
      const known = saved.filter((k) => ALL_KEYS.includes(k));
      const missing = ALL_KEYS.filter((k) => !known.includes(k));
      return [...known, ...missing];
    }
  } catch { /* ignore */ }
  return ALL_KEYS.slice();
}
function loadHidden() {
  try {
    const saved = JSON.parse(localStorage.getItem(HIDDEN_KEY));
    if (Array.isArray(saved)) return new Set(saved);
  } catch { /* ignore */ }
  return new Set(DEFAULT_HIDDEN);
}

export default function TicketTable({ tickets = [], users = [], locName = {}, onOpen, onEdit, onChanged, de = true }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user } = useAuth();
  const canEdit = can(user, "tickets", "edit");
  const canDelete = can(user, "tickets", "delete");
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [order, setOrder] = useState(loadOrder);
  const [hidden, setHidden] = useState(loadHidden);
  const [colMenu, setColMenu] = useState(false);

  useEffect(() => { localStorage.setItem(ORDER_KEY, JSON.stringify(order)); }, [order]);
  useEffect(() => { localStorage.setItem(HIDDEN_KEY, JSON.stringify([...hidden])); }, [hidden]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/tickets/${id}`, { status }); window.dispatchEvent(new Event("tickets:changed")); onChanged && onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const del = async (t, e) => {
    e && e.stopPropagation();
    const ok = await confirm({ title: de ? "Ticket löschen?" : "Delete ticket?", message: t.title, confirmLabel: de ? "Löschen" : "Delete", danger: true });
    if (!ok) return;
    try { await api.delete(`/tickets/${t.id}`); window.dispatchEvent(new Event("tickets:changed")); onChanged && onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const COLS = useMemo(() => ({
    status: {
      label: "Status", sortable: true,
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()}>
          <select data-testid={`ticket-row-status-${t.id}`} value={t.status} disabled={!canEdit} onChange={(e) => setStatus(t.id, e.target.value)}
            className="text-xs font-medium rounded-lg border border-gray-300 bg-white text-gray-800 px-2 py-1.5 focus:outline-none focus:border-[#c00000] disabled:opacity-70">
            {TICKET_STATUS.map((s) => <option key={s.id} value={s.id} className="bg-gray-50 text-gray-900">{de ? s.de : s.en}</option>)}
          </select>
        </div>
      ),
    },
    title: {
      label: de ? "Titel" : "Title", sortable: true,
      render: (t) => {
        const closed = t.status === "closed";
        return (
          <div className="max-w-[380px]">
            <div className={`text-sm font-medium truncate ${closed ? "text-gray-600 line-through" : "text-gray-900"}`} title={t.title}>{t.title}</div>
            {t.note && <div className="text-xs text-gray-600 truncate" title={t.note}>{t.note}</div>}
          </div>
        );
      },
    },
    location: {
      label: de ? "Standort" : "Location", sortable: true,
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()} className="whitespace-nowrap">
          {t.location_id ? (
            <button data-testid={`ticket-row-loc-${t.id}`} onClick={() => navigate(`/locations/${encodeURIComponent(t.location_id)}`)}
              className="inline-flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 transition-colors">
              <MapPin className="w-3 h-3" /> {t.location_id}
            </button>
          ) : dash}
          {locName[t.location_id] && <span className="text-xs text-gray-600 ml-2">{locName[t.location_id]}</span>}
        </div>
      ),
    },
    priority: {
      label: de ? "Priorität" : "Priority", sortable: true,
      render: (t) => { const pm = prioMeta(t.priority); return <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${pm.cls}`}>{de ? pm.de : pm.en}</span>; },
    },
    due_date: {
      label: de ? "Fällig" : "Due", sortable: true,
      render: (t) => {
        const overdue = isOverdue(t);
        return t.due_date ? (
          <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border font-mono whitespace-nowrap ${overdue ? "border-red-500/40 bg-red-500/15 text-red-700" : "border-gray-200 bg-gray-50 text-gray-700"}`}>
            {overdue && <AlertTriangle className="w-3 h-3" />}<Clock className="w-3 h-3" /> {fmtDateOnly(t.due_date)}
          </span>
        ) : dash;
      },
    },
    category: {
      label: de ? "Kategorie" : "Category", sortable: true,
      render: (t) => { const c = categoryMeta(t.category); return <span className="text-[11px] px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-700 whitespace-nowrap">{de ? c.de : c.en}</span>; },
    },
    sla_customer: {
      label: de ? "SLA Kunde" : "Customer SLA", sortable: true,
      render: (t) => slaCell(t.sla_customer_due, t.sla_customer_breached, t.status === "closed", de),
    },
    sla_internal: {
      label: de ? "SLA intern" : "Internal SLA", sortable: true,
      render: (t) => slaCell(t.sla_internal_due, t.sla_internal_breached, t.status === "closed", de),
    },
    device: {
      label: de ? "Gerät" : "Device", sortable: true,
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()}>
          {t.device_code ? (
            <button data-testid={`ticket-row-device-${t.id}`} onClick={() => navigate(`/d/${encodeURIComponent(t.device_code)}`)}
              className="inline-flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors">
              <Monitor className="w-3 h-3" /> {t.device_code}
            </button>
          ) : dash}
        </div>
      ),
    },
    assignee: {
      label: de ? "Zuständig" : "Assignee", sortable: true,
      render: (t) => t.assignee ? (
        <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-700 whitespace-nowrap">
          {t.assignee_type === "group" ? <Users className="w-3 h-3" /> : <User className="w-3 h-3" />} {assigneeLabel(t, users, de)}
        </span>
      ) : dash,
    },
    created_at: {
      label: de ? "Eingang" : "Created", sortable: true,
      render: (t) => <span className="text-sm text-gray-600 font-mono whitespace-nowrap">{fmtDate(t.created_at)}</span>,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [de, users, locName]);

  const visibleKeys = order.filter((k) => !hidden.has(k));

  const sortValue = (t, key) => {
    if (key === "status") return STATUS_RANK[t.status] ?? 0;
    if (key === "priority") return PRIO_RANK[t.priority] ?? 1;
    if (key === "created_at" || key === "due_date") return t[key] ? new Date(t[key]).getTime() : 0;
    if (key === "sla_customer") return t.sla_customer_due ? new Date(t.sla_customer_due).getTime() : Infinity;
    if (key === "sla_internal") return t.sla_internal_due ? new Date(t.sla_internal_due).getTime() : Infinity;
    if (key === "location") return `${t.location_id || ""} ${locName[t.location_id] || ""}`.toLowerCase();
    if (key === "category") return String((categoryMeta(t.category)[de ? "de" : "en"]) || "").toLowerCase();
    if (key === "assignee") return assigneeLabel(t, users, de).toLowerCase();
    if (key === "device") return String(t.device_code ?? "").toLowerCase();
    return String(t[key] ?? "").toLowerCase();
  };
  const toggleSort = (key) => setSort((s) => (s.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : { key: null, dir: "asc" }));
  const rows = useMemo(() => {
    if (!sort.key) return tickets;
    const arr = [...tickets];
    arr.sort((a, b) => cmpVals(sortValue(a, sort.key), sortValue(b, sort.key)));
    if (sort.dir === "desc") arr.reverse();
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, sort, users, locName, de]);

  const toggleHidden = (key) => setHidden((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const moveKey = (key, dir) => setOrder((prev) => {
    const arr = [...prev];
    const i = arr.indexOf(key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= arr.length) return prev;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr;
  });
  const resetCols = () => { setOrder(ALL_KEYS.slice()); setHidden(new Set(DEFAULT_HIDDEN)); };

  const SortTh = ({ colKey }) => {
    const c = COLS[colKey];
    return (
      <th className="px-3 py-3 font-medium whitespace-nowrap">
        {c.sortable ? (
          <button data-testid={`ticket-sort-${colKey}`} onClick={() => toggleSort(colKey)} className="inline-flex items-center gap-1 hover:text-gray-900 transition-colors">
            {c.label}
            {sort.key === colKey ? (sort.dir === "asc" ? <ChevronUp className="w-3 h-3 text-amber-600" /> : <ChevronDown className="w-3 h-3 text-amber-600" />) : <ChevronsUpDown className="w-3 h-3 opacity-30" />}
          </button>
        ) : c.label}
      </th>
    );
  };

  return (
    <div className="card-surface rounded-xl">
      <div className="overflow-auto max-h-[calc(100vh-200px)] rounded-lg border border-gray-100">
        <table className="w-full text-left min-w-max" data-testid="ticket-table">
          <thead className="sticky top-0 bg-gray-50 z-20 shadow-[0_1px_0_0_rgba(255,255,255,0.08)]">
            <tr className="text-xs font-mono uppercase tracking-wider text-gray-600 border-b border-gray-200">
              <th className="px-2 py-3 font-medium w-8 relative">
                <button data-testid="ticket-columns-button" onClick={() => setColMenu(true)} title={de ? "Spalten" : "Columns"}
                  className="text-gray-600 hover:text-gray-900 p-1 rounded-md border border-gray-200 hover:border-gray-300 transition-colors">
                  <Columns3 className="w-3.5 h-3.5" />
                </button>
              </th>
              {visibleKeys.map((k) => <SortTh key={k} colKey={k} />)}
              <th className="px-3 py-3 font-medium text-right">{de ? "Aktionen" : "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => {
              const overdue = isOverdue(t);
              const closed = t.status === "closed";
              return (
                <tr key={t.id} data-testid={`ticket-row-${t.id}`} onClick={() => onOpen && onOpen(t)}
                  className={`border-b border-gray-100 hover:bg-white/[0.03] cursor-pointer transition-colors ${overdue && !closed ? "bg-red-500/[0.06]" : ""}`}>
                  <td className="px-2 py-3"></td>
                  {visibleKeys.map((k) => <td key={k} className="px-3 py-3">{COLS[k].render(t)}</td>)}
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      {canEdit && <button data-testid={`ticket-row-edit-${t.id}`} onClick={() => onEdit && onEdit(t)} className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"><Pencil className="w-4 h-4" /></button>}
                      {canDelete && <button data-testid={`ticket-row-delete-${t.id}`} onClick={(e) => del(t, e)} className="p-1.5 text-gray-600 hover:text-red-700 hover:bg-red-500/10 rounded transition-colors"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={colMenu} onOpenChange={setColMenu}>
        <DialogContent data-testid="ticket-columns-menu" className="max-w-xl max-h-[88vh] flex flex-col bg-gray-50 border-gray-200 text-gray-900">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              <Columns3 className="w-5 h-5 text-amber-600" />
              {de ? "Spalten konfigurieren" : "Configure columns"}
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {de
                ? "Ziehe die Zeilen am Griff, um die Reihenfolge zu ändern. Mit dem Auge blendest du Spalten ein oder aus."
                : "Drag rows by the handle to reorder. Use the eye to show or hide columns."}
            </DialogDescription>
          </DialogHeader>

          <Reorder.Group axis="y" values={order} onReorder={setOrder} className="space-y-1.5 flex-1 min-h-0 overflow-y-auto pr-1 -mr-1">
            {order.map((k, idx) => (
              <ColRow
                key={k} colKey={k} label={COLS[k].label} shown={!hidden.has(k)}
                isFirst={idx === 0} isLast={idx === order.length - 1} de={de}
                onUp={() => moveKey(k, -1)} onDown={() => moveKey(k, 1)} onToggle={() => toggleHidden(k)}
              />
            ))}
          </Reorder.Group>

          <div className="shrink-0 flex items-center justify-between pt-2 border-t border-gray-200">
            <button data-testid="ticket-cols-reset" onClick={resetCols}
              className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
              <RotateCcw className="w-4 h-4" /> {de ? "Zurücksetzen" : "Reset"}
            </button>
            <span className="text-xs text-gray-600 font-mono">
              {order.length - hidden.size}/{order.length} {de ? "sichtbar" : "visible"}
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ColRow({ colKey, label, shown, isFirst, isLast, de, onUp, onDown, onToggle }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      value={colKey}
      dragListener={false}
      dragControls={controls}
      data-testid={`ticket-col-row-${colKey}`}
      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors select-none ${shown ? "bg-white/[0.04] border-gray-200" : "bg-transparent border-gray-100 opacity-60"}`}
    >
      <button
        type="button"
        onPointerDown={(e) => controls.start(e)}
        data-testid={`ticket-col-drag-${colKey}`}
        title={de ? "Ziehen zum Ordnen" : "Drag to reorder"}
        className="cursor-grab active:cursor-grabbing touch-none text-gray-600 hover:text-gray-900 p-1 -ml-1 rounded"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span className="text-gray-800 truncate flex-1 min-w-0 text-sm">{label}</span>
      <button data-testid={`ticket-col-up-${colKey}`} disabled={isFirst} onClick={onUp} title={de ? "Nach oben" : "Move up"}
        className="p-1.5 rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-25 disabled:hover:bg-transparent transition-colors">
        <ChevronUp className="w-4 h-4" />
      </button>
      <button data-testid={`ticket-col-down-${colKey}`} disabled={isLast} onClick={onDown} title={de ? "Nach unten" : "Move down"}
        className="p-1.5 rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-25 disabled:hover:bg-transparent transition-colors">
        <ChevronDown className="w-4 h-4" />
      </button>
      <button data-testid={`ticket-col-toggle-${colKey}`} onClick={onToggle} title={shown ? (de ? "Ausblenden" : "Hide") : (de ? "Einblenden" : "Show")}
        className={`p-1.5 rounded transition-colors ${shown ? "text-[#c00000] hover:bg-[#c00000]/10" : "text-gray-600 hover:bg-gray-100"}`}>
        {shown ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>
    </Reorder.Item>
  );
}
